import { NODATA } from '../../utils/consts'
import { determineClosest, normalizeWhiteSpace, rankOrderWithValue } from '../../utils/general'
import { Maybe, NumericProperties } from '../../utils/common.types'
import { getPgnPropertyDefinition } from './definitions'
import { getMetaLabel } from './meta'
import { MetaData, PgnData, PgnNumber } from './types'

/**
 * Returns true if the passed value is not null, undefined or NODATA false otherwise.
 */
export const isValidPgnValue = (val: number): boolean => (
  !isNaN(val)
  && isFinite(val)
  && val !== NODATA
)


/**
 * Creates a function that can be used to to validate a PGN data object.
 * If any of the corresponding keys values are null, undefined or NODATA the validator will return false true otherwise.
 * 
 * Returns: object => boolean
 */
export const createHasDataValidator = <T extends PgnData>(keys: NumericProperties<T>[]) => (dataObj: T) => {
  for (const k of keys) {
    if (!isValidPgnValue(dataObj[k] as number)) {
      return false
    }
  }

  return true
}

/**
 * Finds the user friendly label for a pgn sub option. 
 */
export const getDescriptivePgnPropertyLabel = (pgn: PgnNumber, propertyKey: string, meta: Maybe<MetaData> = null, dataSrcLabel: Maybe<string> = null) => {
  const { getLabel } = getPgnPropertyDefinition(pgn, propertyKey) || {}
  if (!getLabel) {
    return null
  }

  const label = getLabel(meta)

  // Trim the dataSrcLabel of any whitespaces
  dataSrcLabel = !!dataSrcLabel ? normalizeWhiteSpace(dataSrcLabel) : null

  let metaStr = ''
  if (!!meta) {
    const metaLabel = getMetaLabel(meta, pgn)
    metaStr = metaLabel ? ` (${metaLabel})` : ''
  }

  return `${label}${dataSrcLabel ? ` (${dataSrcLabel})` : ''}${metaStr}`
}

export const combinePgnData = <T extends PgnData>(maxTimeDiff: number, ...args: ReadonlyArray<ReadonlyArray<T>>): T[] => {
  if (args.length < 1) {
    return []
  } else if (args.length === 1) {
    return [...args[0]]
  }

  const arrCombined: T[] = []

  const ranksArrays: number[][] = new Array<number[]>(args.length - 1)
  const mappedArrays: T[][] = new Array(args.length - 1)
  for (let i = 0; i < mappedArrays.length; i++) {
    const curr = rankOrderWithValue(args[i + 1], x => x.T)

    mappedArrays[i] = []
    ranksArrays[i] = []
    for (let j = 0; j < curr.length; j++) {
      const { element, rank } = curr[j]
      mappedArrays[i][j] = element
      ranksArrays[i][j] = rank
    }
  }

  const arrFirst = args[0]
  for (let i = 0; i < arrFirst.length; i++) {
    const { T, ...rest } = arrFirst[i]
    let combined: T = { T, ...rest } as T
  
    for (let j = 0; j < mappedArrays.length; j++) {
      const index = determineClosest(ranksArrays[j], T, maxTimeDiff)
      if (index > -1) {
        const copy = { ...(mappedArrays[j][index]) }

        combined = {
          ...rest,
          ...copy,
          T
        }
      }
    }

    arrCombined.push(combined)
  }

  return arrCombined
}
